import { VisitScheduleItem } from "../../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import Modal from "../../misc/components/modal";
import * as React from "react";
import { useVisitScheduleContext } from "./VisitScheduleContext";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";

export interface ImportScheduleModalProps {
    onClose: () => void;
    onSave: (schedule: VisitScheduleItem[]) => void;
};

export const ImportScheduleModal = ({ onClose, onSave }: ImportScheduleModalProps) => {
    const [schedule, setSchedule] = React.useState<string | undefined>();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>();
    const reader = new FileReader();
    const { sessionTypes, deviceTypes } = useVisitScheduleContext();

    const onFileChange = (selectedFile) => {
        setError(undefined);
        if (selectedFile) {
            setLoading(true);
            reader.readAsText(selectedFile)
        } else {
            setSchedule(undefined);
        }
    }

    reader.onload = (event) => {
        setSchedule(event.target.result as string);
        setLoading(false);
    };

    const onSaveClick = async () => {
        try {
            const scheduleObject = JSON.parse(schedule) as VisitScheduleItem[];
            if (scheduleObject === undefined || scheduleObject === null || scheduleObject?.length === 0) {
                setError("Invalid JSON File");
                setLoading(false);
                return;
            }

            if (scheduleObject.filter(x => !x.VisitScheduleId).length > 0) {
                setError("VisitScheduleId is required for each visit");
                setLoading(false);
                return;
            }

            if (scheduleObject.filter(x => x.Sessions.some(s => !s.VisitScheduleItemId)).length > 0) {
                setError("VisitScheduleItemId is required for each session");
                setLoading(false);
                return;
            }

            const invalidItemsDeviceTypes = scheduleObject.filter(v =>
                v.Sessions.some(s =>
                    !deviceTypes.map(dt => dt.Id.toUpperCase()).includes(s.DeviceTypeId.toUpperCase())
                )
            )
                .flatMap(s => s.Sessions.flatMap(se => se.DeviceTypeId));

            if (invalidItemsDeviceTypes.length > 0) {
                setError(`Study schedule contains device types are not supported by this study '${invalidItemsDeviceTypes.join(", ")}'`);
                setLoading(false);
                return;
            }

            const invalidItemsSessionTypes = scheduleObject.filter(v =>
                v.Sessions.some(s =>
                    !sessionTypes.map(dt => dt.Id.toUpperCase()).includes(s.SessionTypeId.toUpperCase())
                )
            )
                .flatMap(s => s.Sessions.flatMap(se => se.SessionTypeId));

            if (invalidItemsSessionTypes.length > 0) {
                setError(`Study schedule contains session types are not supported by this study '${invalidItemsSessionTypes.join(", ")}'`);
                setLoading(false);
                return;
            }

            const invalidDiaryItemsSet = scheduleObject.filter(so => so.Sessions.some(s => (s.IncludedInDailySummaryCompliance) && s.SessionTypeId.toUpperCase() !== SessionTypes.EDiary));
            if (invalidDiaryItemsSet.length > 0) {
                setError("Included in daily summary compliance can only be set for eDiary sessions");
                setLoading(false);
                return;
            }

            const invalidResetDiaryItemsSet = scheduleObject.filter(so => so.Sessions.some(s => (s.ResetDiaryOnUpload) && s.SessionTypeId.toUpperCase() !== SessionTypes.EDiary));
            if (invalidResetDiaryItemsSet.length > 0) {
                setError("Reset Diary On USB Upload can only be set for eDiary sessions");
                setLoading(false);
                return;
            }

            onSave(scheduleObject)
            setError(undefined);
        } catch (error) {
            setError("The selected JSON file does not match the format expected")
            setLoading(false);
        }
    }

    return (
        <Modal show={true} header="Import Study Schedule">
            {error ? <Alert show={true} category={AlertCategoryEnum.Danger}>{error}</Alert> : null}
            <div className="row">
                <div className="col-md-12">
                    <div className="panel-body">
                        <div className="form-group">
                            <input type="file" className="form-control" accept=".json" onChange={(e) => onFileChange(e.target.files[0])} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" type="submit" disabled={loading || error !== undefined} onClick={onSaveClick}>
                    Import
                </button> &nbsp;
                <button className="btn btn-primary btn-outline" disabled={loading} onClick={onClose}>
                    Close
                </button>
            </div>
        </Modal>
    );
}
